import { Commit, createStore, Store } from 'vuex';
import {
  BusEvents,
  LocalStorageKeys,
  RNPayloadActions,
  RouteNames,
  StationNotificationTypes,
  StoreActions,
  StoreMutations,
} from '@/helpers/enums';
import constants from '@/helpers/constants';
import { captureExceptionToSentry, getToken, userData } from '@/helpers/utils';
import router from '@/router';
import {
  FacilityWorker,
  FacilityWorkerToken,
} from '@/providers/laundryheap/interfaces/facility-worker';
import { Facility, RNFacilityOrderBag } from '@/interfaces';
import { connectFacilityOrderBagTag } from '@/providers/laundryheap/api-helpers';
import {
  FacilityOrder,
  FacilityOrderBag,
  FacilityOrders,
} from '@/providers/laundryheap/interfaces/facility-orders';
import eventBus from '@/eventBus';
import {
  OrderListPreselect,
  RegistrationData,
  RNCameraActionReturnPayload,
  StationNotification,
  UpdateAnnotationImagePayload,
} from '@/helpers/types';
import { FacilityService } from '@/providers/laundryheap/interfaces/facility-services';
import { i18n } from '@/i18n';
import { StoreMissingData } from '@/errors/store-missing-data';
import {
  getFacilityOrders,
  getFacilityOrdersCached,
  getFacilityOrdersIncremental,
} from '@/providers/laundryheap/api';
import Clarity from '@microsoft/clarity';

export interface StorePayload {
  action: string;
  data: Record<string, unknown>;
}

export interface StationState {
  loader: boolean;
  stateKey: number;
  resetComponentData: number;
  facility: Facility | null;
  worker: FacilityWorker | null;
  facilityBagServices: FacilityService[];
  facilityOrderBag: FacilityOrderBag | null;
  facilityOrder: FacilityOrder;
  connected: boolean;
  annotateImages: string[];
  alreadyScannedInBags: string[];
  notifications: StationNotification[];
  previousRoute: string | null;
  registrationData: RegistrationData;
  keyboardHeight: number;
  keyboardOpen: boolean;
  bagPreviewMode: boolean;
  orderListPreselect: OrderListPreselect | null;
  appError: string | null;
  supportNotification: number | null;
  facilityOrders: FacilityOrders | null;
  disableHardwareBackButtonNavigation: boolean;
  cameraRequests: Record<
    string,
    (payload: RNCameraActionReturnPayload) => void
  >;
}

const syncWithShell = async (store: Store<StationState>): Promise<void> => {
  // called when the store is initialized
  // add shell message handler
  window.onShellMessage = await handleShellMessage;

  if (window.ReactNativeWebView) {
    store.commit('setConnected', true);
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        action: 'ping',
        data: 'vue app connected',
      })
    );

    // Update RN with login data
    const token = getToken();
    const worker = userData.data;
    if (token && worker) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          action: RNPayloadActions.store_update,
          data: { token, userData: worker },
        })
      );
    }
  } else {
    store.commit('setConnected', false);
  }
};

const handleShellMessage = async (message: string): Promise<boolean> => {
  await store.dispatch(StoreActions.onShellMessage, message);
  return true;
};

export const store = createStore({
  state: {
    loader: false,
    stateKey: 0, // The key used for refreshing the components
    resetComponentData: 0,
    facility: null,
    worker: null,
    facilityBagServices: [] as FacilityService[],
    facilityOrderBag: null as FacilityOrderBag | null,
    facilityOrder: {} as FacilityOrder,
    alreadyScannedInBags: [] as string[],
    connected: false,
    annotateImages: [],
    notifications: [] as StationNotification[],
    previousRoute: null as string | null,
    registrationData: {} as RegistrationData,
    keyboardHeight: 0,
    keyboardOpen: false,
    bagPreviewMode: false,
    orderListPreselect: null as OrderListPreselect | null,
    appError: null as string | null,
    supportNotification: null as number | null, // Count of unread problems
    facilityOrders: null,
    disableHardwareBackButtonNavigation: false,
    cameraRequests: {},
  } as StationState,
  getters: {
    facilityOrders: (state: StationState) => state.facilityOrders,
  },
  mutations: {
    showLoader(state: StationState) {
      state.loader = true;
    },
    hideLoader(state: StationState) {
      state.loader = false;
    },
    setAppError(state: StationState, data: string) {
      state.appError = data;
    },
    resetAppError(state: StationState) {
      state.appError = null;
    },
    setSupportNotification(state: StationState, data: number) {
      state.supportNotification = data;
    },
    resetSupportNotification(state: StationState) {
      state.supportNotification = null;
    },
    addFacilityBagServices(state: StationState, data: FacilityService[]) {
      state.facilityBagServices = data;
    },
    resetFacilityBagServices(state: StationState) {
      state.facilityBagServices = [];
    },
    addFacilityOrderBag(state: StationState, data: FacilityOrderBag) {
      state.facilityOrderBag = data;
    },
    resetFacilityOrderBag(state: StationState) {
      state.facilityOrderBag = null;
    },
    addFacilityOrder(state: StationState, data: FacilityOrder) {
      state.facilityOrder = data;
    },
    setConnected(state: StationState, connected: boolean) {
      state.connected = connected;
    },
    setAnnotationImage(state: StationState, images: string[]) {
      state.annotateImages = images;
    },
    updateAnnotationImage(
      state: StationState,
      data: UpdateAnnotationImagePayload
    ) {
      state.annotateImages[data.index] = data.photo;
    },
    addAnnotateImage(state: StationState, image: string) {
      state.annotateImages.push(image);
    },
    resetAnnotationImage(state: StationState) {
      state.annotateImages = [];
    },
    setAlreadyScannedInBags(state: StationState, tags: string[]) {
      state.alreadyScannedInBags = tags;
    },
    addAlreadyScannedInBags(state: StationState, tag: string) {
      state.alreadyScannedInBags.push(tag);
    },
    removeAlreadyScannedInBags(state: StationState, tag: string) {
      state.alreadyScannedInBags = state.alreadyScannedInBags.filter(
        (t) => t !== tag
      );
    },
    resetAlreadyScannedInBags(state: StationState) {
      state.alreadyScannedInBags = [];
    },
    addNotification(state: StationState, notification: StationNotification) {
      state.notifications.push(notification);
    },
    clearNotifications(state: StationState) {
      state.notifications = [];
    },
    hideNotification(state: StationState, notificationId: string) {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== notificationId
      );
    },
    setKeyboardHeight(state: StationState, value: number) {
      state.keyboardHeight = value;
    },
    setKeyboardShown(state: StationState) {
      state.keyboardOpen = true;
    },
    setKeyboardHidden(state: StationState) {
      state.keyboardOpen = false;
    },
    showBagPreviewMode(state: StationState) {
      state.bagPreviewMode = true;
    },
    hideBagPreviewMode(state: StationState) {
      state.bagPreviewMode = false;
    },
    resetPreviousRoute(state) {
      state.previousRoute = null;
    },
    setPreviousRoute(state: StationState, route: string | null) {
      state.previousRoute = route;
    },
    /* Registration */
    setRegistrationQRCode(state: StationState, qrCode: string | null) {
      state.registrationData = { qrCode: qrCode };
    },
    setRegistrationFacility(state: StationState, facility: Facility) {
      state.registrationData = {
        ...state.registrationData,
        facility: facility,
      };
    },
    setRegistrationData(state: StationState, data: RegistrationData | null) {
      state.registrationData = data || {};
    },
    setOrderListPreselect(
      state: StationState,
      preselect: OrderListPreselect | null
    ) {
      state.orderListPreselect = preselect;
    },
    setFacility(state: StationState, facility: Facility): void {
      state.facility = facility;
    },
    setFacilityOrders(
      state: StationState,
      facilityOrders: FacilityOrders | null
    ) {
      if (!facilityOrders) {
        state.facilityOrders = null;
        return;
      }
      state.facilityOrders = { ...state.facilityOrders, ...facilityOrders };
    },
    disableHardwareBackButtonNavigation(state: StationState) {
      state.disableHardwareBackButtonNavigation = true;
    },
    enableHardwareBackButtonNavigation(state: StationState) {
      state.disableHardwareBackButtonNavigation = false;
    },
    createCameraRequest(
      state: StationState,
      data: { requestKey: string; handler: (payload: RNCameraActionReturnPayload) => void }
    ) {
      state.cameraRequests[data.requestKey] = data.handler;
    },
    removeCameraRequest(state: StationState, key: string) {
      if (!state.cameraRequests[key]) {
        return;
      }
      delete state.cameraRequests[key];
    },
  },
  actions: {
    async onShellMessage({ commit }, payload: StorePayload) {
      if (payload && payload.action) {
        switch (payload.action) {
          case 'notification':
            setNotificationData(commit, payload.data as StationNotification);
            break;
          case 'clicked_bg_notification':
            switch (payload.data.type as StationNotificationTypes) {
              default:
              case StationNotificationTypes.new_reply:
                if (!payload.data?.id) {
                  return;
                }

                return await router.push({
                  name: RouteNames.supportOrderProblemList,
                  params: {
                    facilityOrderId: payload.data.id as string,
                  },
                });
              case StationNotificationTypes.order_delayed:
                if (!payload.data?.id) {
                  return;
                }

                return await router.push({
                  name: RouteNames.orderDetails,
                  params: {
                    facilityOrderId: payload.data?.id as string,
                  },
                });
              case StationNotificationTypes.orders_delayed:
                commit(StoreMutations.setOrderListPreselect, {
                  prioritised: true,
                } as OrderListPreselect);

                return await router.push({ name: RouteNames.orders });
              case StationNotificationTypes.survey_notification:
                if (!payload?.data?.url) {
                  return;
                }
                setNotificationData(
                  commit,
                  payload.data as StationNotification
                );
                break;
            }

            break;
          case 'trigger_tracking_event':
            if (typeof payload.data === 'object') {
              Object.keys(payload.data).forEach((key) => {
                Clarity.setTag(key, String(payload.data[key]));
              });
            }
            break;
          case 'on_load':
            if (payload.data.mobileAppData) {
              localStorage.setItem(
                LocalStorageKeys.mobileAppData,
                JSON.stringify(payload.data.mobileAppData)
              );
            }
            // Insets for mobile
            if (payload.data.mobileAppInsets) {
              localStorage.setItem(
                LocalStorageKeys.mobileAppInsets,
                JSON.stringify(payload.data.mobileAppInsets)
              );
            }
            // Login data
            if (payload.data.woker) {
              const worker = payload.data.woker as FacilityWorkerToken;
              localStorage.setItem(LocalStorageKeys.token, worker.token);
              if (worker.facilityWorker) {
                userData.data = worker.facilityWorker;
              }
            }
            break;
          case 'qr_scanner_back': {
            // Trigger camera closed event
            eventBus.emit(BusEvents.camera_closed);

            // If we started loader and went back from camera
            commit(StoreMutations.hideLoader);
            break;
          }
          case 'qr_scanner_close': {
            // If we started loader and went back from camera
            commit(StoreMutations.hideLoader);
            break;
          }
          case 'go_back': {
            if (this.state.disableHardwareBackButtonNavigation) {
              break;
            }
            if (this.state.previousRoute) {
              return await router
                .push({ path: this.state.previousRoute })
                .catch(async (e): Promise<void> => {
                  captureExceptionToSentry(e);
                  await router.back();
                });
            }

            await router.back();
            break;
          }
          case 'scan_in_bag': {
            if (!payload.data?.bag) {
              throw new StoreMissingData(payload);
            }

            const bag = payload.data.bag as RNFacilityOrderBag;
            commit(StoreMutations.hideBagPreviewMode);
            await router.push({
              name: RouteNames.bagItemisation,
              params: { bagId: bag.id },
            });
            break;
          }
          /**
           * Default open bag details
           */
          case 'open_bag_details': {
            if (!payload.data?.bag) {
              throw new StoreMissingData(payload);
            }

            const bag = payload.data.bag as RNFacilityOrderBag;
            await router.push({
              name: RouteNames.bagDetails,
              params: { facilityBagId: bag.id },
            });
            break;
          }
          /**
           * When user scans different bag from the same order, we show popup
           * with this bag details
           */
          case 'scan_in_bag_different_bag': {
            if (!payload.data?.bag) {
              throw new StoreMissingData(payload);
            }

            const bag = payload.data.bag as RNFacilityOrderBag;
            commit(StoreMutations.showBagPreviewMode);
            await router.push({
              name: RouteNames.bagDetails,
              params: { facilityBagId: bag.id },
            });
            break;
          }
          /**
           * When user switches from preview screen to fullscreen for bag details
           */
          case 'scan_in_bag_different_bag_fullscreen': {
            commit(StoreMutations.hideBagPreviewMode);
            break;
          }
          case 'scan_in_packages': {
            if (payload.data?.alreadyScannedTagList === undefined) {
              throw new StoreMissingData(payload);
            }

            commit(
              StoreMutations.setAlreadyScannedInBags,
              payload.data.alreadyScannedTagList
            );
            break;
          }
          case 'scan_in_package': {
            if (payload.data?.alreadyScannedTagList === undefined) {
              throw new StoreMissingData(payload);
            }

            commit(
              StoreMutations.addAlreadyScannedInBags,
              payload.data.alreadyScannedTagList
            );
            break;
          }
          case 'add_connected_tag': {
            if (!payload.data?.bagId || !payload.data?.code) {
              throw new StoreMissingData(payload);
            }

            const bagId = payload.data.bagId as string;
            const code = payload.data.code as string;
            const serviceShortCode = payload.data.serviceShortCode as string;

            const newBag = await connectFacilityOrderBagTag(
              bagId,
              code,
              serviceShortCode
            );
            commit(StoreMutations.addFacilityOrderBag, newBag);
            commit(StoreMutations.addFacilityOrder, newBag.facilityOrder);
            if (payload.data?.storeToScannedInTags) {
              commit(StoreMutations.addAlreadyScannedInBags, payload.data.code);
            }
            commit(StoreMutations.hideLoader);
            break;
          }
          case 'annotate': {
            /* openCameraV2 support */
            if (payload.data.requestKey) {
              const handler = (
                this.state.cameraRequests as StationState['cameraRequests']
              )[payload.data.requestKey as string];
              handler(payload.data as RNCameraActionReturnPayload);
              break;
            } else {
              if (payload.data.imageIndex !== undefined) {
                commit(StoreMutations.updateAnnotationImage, {
                  photo: payload.data.image,
                  index: payload.data.imageIndex,
                } as UpdateAnnotationImagePayload);
              } else {
                commit(StoreMutations.addAnnotateImage, payload.data.image);
              }
              break;
            }
          }
          case 'start_loading_screen': {
            // Show loader when RN starts loading api
            commit(StoreMutations.showLoader);
            break;
          }
          case 'end_loading_screen': {
            // Hide loader when RN finishes
            commit(StoreMutations.hideLoader);
            break;
          }
          case 'login_error': {
            // Hide loader when RN finishes
            commit(StoreMutations.hideLoader);

            commit(
              StoreMutations.setAppError,
              i18n.global.t('common.errors.login_error')
            );
            break;
          }
          case 'login':
            if (payload.data.token) {
              await router.push({
                name: RouteNames.login,
                params: { magicLinkToken: payload.data.token as string },
              });
              commit(StoreMutations.hideLoader);
              return;
            }

            commit(
              StoreMutations.setAppError,
              i18n.global.t('common.errors.login_error')
            );
            commit(StoreMutations.hideLoader);
            break;
          case 'keyboard':
            if (
              typeof payload?.data?.shown !== 'boolean' ||
              typeof payload?.data?.height !== 'number'
            ) {
              console.error('Invalid call to keyboard store function');
            }

            commit(
              StoreMutations.setKeyboardHeight,
              payload?.data?.height || 0
            );

            commit(
              payload?.data?.shown
                ? StoreMutations.setKeyboardShown
                : StoreMutations.setKeyboardHidden
            );
            break;
          case 'logout':
            localStorage.removeItem(LocalStorageKeys.token);
            localStorage.removeItem(LocalStorageKeys.userData);
            await router.push({ name: RouteNames.registrationWelcome });
            break;
          case 'registration_bag_scan':
            commit(StoreMutations.setRegistrationQRCode, payload.data.code);
            break;
        }
      }
    },
    updateFacilityOrders: async ({ state, commit }): Promise<void> => {
      const cachedOrders = getFacilityOrdersCached();
      if (cachedOrders) {
        commit(StoreMutations.setFacilityOrders, cachedOrders);
      }

      if (state.facilityOrders) {
        await getFacilityOrdersIncremental({
          existingOrders: state.facilityOrders,
          callback: (orders) => {
            commit(StoreMutations.setFacilityOrders, orders);
          },
        });
      } else {
        const response = await getFacilityOrders();
        if (response.data && response.status) {
          commit(StoreMutations.setFacilityOrders, response.data);
        }
      }
    },
  },
  modules: {},
  plugins: [syncWithShell],
});

// Defaults for setting notification
function setNotificationData(commit: Commit, data: StationNotification): void {
  commit(StoreMutations.addNotification, {
    id: data.id || Date.now(),
    type: data.type || 'new_reply',
    timeout: data.timeout || constants.defaultNotificationDuration,
    title: data.title || null,
    data: data,
  });
}

export default store;
